export interface SalesRegistrationStorageItem {
  id: string;
  name: string;
  barkodas?: string;
  prekesKodas?: string;
  category: string;
  quantity: number;
  storeQuantity?: number;
  savikaina?: number;
  pardavimoKaina?: number;
  supplier?: string;
}

/** Stock pool used for sales registration: Parduotuvė for souvenirs, Sandėliukas otherwise. */
export function getSalesStockQuantity(item: SalesRegistrationStorageItem): number {
  if (item.category === 'souvenirs') {
    return item.storeQuantity ?? 0;
  }
  return item.quantity;
}

export function usesStoreStockForSales(category: string): boolean {
  return category === 'souvenirs';
}

export interface ParsedSale {
  prekesKodas: string;
  kiekis: number;
  lineNumber: number;
}

export interface ProcessedSale extends ParsedSale {
  preke: string;
  matchedItem: SalesRegistrationStorageItem | null;
  currentQuantity: number;
  newQuantity: number;
  barkodas?: string;
  status: 'matched' | 'unmatched' | 'insufficient_stock';
  /** Number of pasted lines combined into this grouped preview row */
  sourceLineCount?: number;
}

export interface SalesRegistrationApiItem {
  itemId: string;
  quantity: number;
  type: 'in' | 'out';
  notes: string;
}

/** Parse tab-separated sales rows. Last column is quantity; preceding columns form prekės kodas. */
export function parseSalesData(text: string): ParsedSale[] {
  const lines = text.trim().split('\n');
  const parsed: ParsedSale[] = [];

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return;

    const parts = trimmedLine.split('\t').map((p) => p.trim());
    if (parts.length < 2) return;

    const kiekis = parseInt(parts[parts.length - 1], 10);
    const prekesKodas = parts.slice(0, -1).join(' ').trim();

    if (!prekesKodas || Number.isNaN(kiekis) || kiekis === 0) return;

    parsed.push({
      prekesKodas,
      kiekis,
      lineNumber: index + 1
    });
  });

  return parsed;
}

export function findSalesItemMatch(
  prekesKodas: string,
  storageItems: SalesRegistrationStorageItem[]
): SalesRegistrationStorageItem | null {
  const trimmed = prekesKodas.trim();

  let match = storageItems.find(
    (si) => si.prekesKodas != null && si.prekesKodas.trim() === trimmed
  );
  if (match) return match;

  const parts = trimmed.split(/\s+/);
  const extractedCode = parts.length > 1 ? parts[parts.length - 1] : trimmed;
  match = storageItems.find(
    (si) => si.prekesKodas != null && si.prekesKodas.trim() === extractedCode
  );
  if (match) return match;

  match = storageItems.find(
    (si) =>
      (si.name + ' ' + (si.prekesKodas || '')).trim() === trimmed ||
      si.name?.trim() === trimmed
  );
  return match || null;
}

/** Match parsed rows to storage items with per-row running balance and aggregate stock validation. */
export function matchSalesItems(
  parsed: ParsedSale[],
  storageItems: SalesRegistrationStorageItem[]
): ProcessedSale[] {
  const initial = parsed.map((item) => {
    const matchedItem = findSalesItemMatch(item.prekesKodas, storageItems);
    const currentQuantity = matchedItem ? getSalesStockQuantity(matchedItem) : 0;
    const preke = matchedItem?.name ?? item.prekesKodas;
    return {
      ...item,
      preke,
      matchedItem,
      currentQuantity,
      newQuantity: currentQuantity - item.kiekis,
      barkodas: matchedItem?.barkodas,
      status: 'matched' as const
    };
  });

  const aggregateByItem = new Map<
    string,
    { totalOut: number; totalIn: number; currentQuantity: number }
  >();
  for (const row of initial) {
    if (!row.matchedItem) continue;
    const id = row.matchedItem.id;
    if (!aggregateByItem.has(id)) {
      aggregateByItem.set(id, { totalOut: 0, totalIn: 0, currentQuantity: row.currentQuantity });
    }
    const agg = aggregateByItem.get(id)!;
    if (row.kiekis > 0) agg.totalOut += row.kiekis;
    else if (row.kiekis < 0) agg.totalIn += Math.abs(row.kiekis);
  }

  const pasteSimStock = new Map<string, number>();
  const newQuantityByLine = new Map<number, number>();
  for (const row of initial) {
    if (!row.matchedItem) {
      newQuantityByLine.set(row.lineNumber, row.newQuantity);
      continue;
    }
    const id = row.matchedItem.id;
    if (!pasteSimStock.has(id)) {
      pasteSimStock.set(id, row.currentQuantity);
    }
    const stock = pasteSimStock.get(id)!;
    if (row.kiekis > 0) {
      const after = stock - row.kiekis;
      pasteSimStock.set(id, after);
      newQuantityByLine.set(row.lineNumber, after);
    } else {
      const after = stock + Math.abs(row.kiekis);
      pasteSimStock.set(id, after);
      newQuantityByLine.set(row.lineNumber, after);
    }
  }

  const matchedRows = initial.filter((r) => r.matchedItem != null);
  const regOrder = sortSalesForRegistration(matchedRows);
  const regSimStock = new Map<string, number>();
  const failedLines = new Set<number>();

  for (const row of regOrder) {
    const id = row.matchedItem!.id;
    if (!regSimStock.has(id)) {
      regSimStock.set(id, row.currentQuantity);
    }
    const stock = regSimStock.get(id)!;
    if (row.kiekis > 0) {
      if (stock < row.kiekis) {
        failedLines.add(row.lineNumber);
      } else {
        regSimStock.set(id, stock - row.kiekis);
      }
    } else {
      regSimStock.set(id, stock + Math.abs(row.kiekis));
    }
  }

  return initial.map((row) => {
    let status: 'matched' | 'unmatched' | 'insufficient_stock' = row.matchedItem
      ? 'matched'
      : 'unmatched';

    if (row.matchedItem) {
      const agg = aggregateByItem.get(row.matchedItem.id)!;
      const netNeeded = agg.totalOut - agg.totalIn;
      const aggregateInsufficient = row.kiekis > 0 && row.currentQuantity < netNeeded;

      if (failedLines.has(row.lineNumber) || aggregateInsufficient) {
        status = 'insufficient_stock';
      } else {
        status = 'matched';
      }
    }

    return {
      ...row,
      newQuantity: newQuantityByLine.get(row.lineNumber) ?? row.newQuantity,
      status
    };
  });
}

function getProcessedSaleGroupKey(row: ProcessedSale): string {
  if (row.matchedItem) return row.matchedItem.id;
  return `unmatched:${row.prekesKodas.trim().toLowerCase()}`;
}

/** Combine duplicate product rows for preview; registration still uses raw per-line rows. */
export function groupProcessedSales(rows: ProcessedSale[]): ProcessedSale[] {
  const groups = new Map<string, ProcessedSale[]>();
  const groupOrder: string[] = [];

  for (const row of rows) {
    const key = getProcessedSaleGroupKey(row);
    if (!groups.has(key)) {
      groups.set(key, []);
      groupOrder.push(key);
    }
    groups.get(key)!.push(row);
  }

  const grouped: ProcessedSale[] = [];

  for (const key of groupOrder) {
    const groupRows = groups.get(key)!;
    const first = groupRows[0];
    const sumKiekis = groupRows.reduce((sum, r) => sum + r.kiekis, 0);

    if (sumKiekis === 0) continue;

    const totalOut = groupRows.filter((r) => r.kiekis > 0).reduce((sum, r) => sum + r.kiekis, 0);
    const totalIn = groupRows
      .filter((r) => r.kiekis < 0)
      .reduce((sum, r) => sum + Math.abs(r.kiekis), 0);
    const netNeeded = totalOut - totalIn;

    let status: 'matched' | 'unmatched' | 'insufficient_stock' = first.matchedItem
      ? 'matched'
      : 'unmatched';

    if (first.matchedItem) {
      const hasInsufficientLine = groupRows.some((r) => r.status === 'insufficient_stock');
      const aggregateInsufficient = netNeeded > 0 && first.currentQuantity < netNeeded;
      if (hasInsufficientLine || aggregateInsufficient) {
        status = 'insufficient_stock';
      }
    }

    grouped.push({
      ...first,
      kiekis: sumKiekis,
      newQuantity: first.currentQuantity - sumKiekis,
      status,
      sourceLineCount: groupRows.length,
      lineNumber: first.lineNumber
    });
  }

  return grouped;
}

/** Sort returns before sales per item so backend sequential validation succeeds when net qty is valid. */
export function sortSalesForRegistration<T extends { matchedItem: { id: string } | null; kiekis: number }>(
  rows: T[]
): T[] {
  return [...rows].sort((a, b) => {
    const aId = a.matchedItem?.id ?? '';
    const bId = b.matchedItem?.id ?? '';
    if (aId !== bId) return 0;

    const aReturn = a.kiekis < 0 ? 0 : 1;
    const bReturn = b.kiekis < 0 ? 0 : 1;
    return aReturn - bReturn;
  });
}

export function buildSalesRegistrationItems(
  processedSales: ProcessedSale[]
): SalesRegistrationApiItem[] {
  const matched = processedSales.filter(
    (ps): ps is ProcessedSale & { matchedItem: SalesRegistrationStorageItem } =>
      ps.status === 'matched' && ps.matchedItem != null
  );

  const sorted = sortSalesForRegistration(matched);

  return sorted.map((item) => {
    const isRetract = item.kiekis < 0;
    const type = isRetract ? 'in' : 'out';
    const quantity = isRetract ? Math.abs(item.kiekis) : item.kiekis;
    const notes = isRetract
      ? `Atšaukti pardavimas (įvesta): +${Math.abs(item.kiekis)} vnt`
      : `Pardavimas: -${item.kiekis} vnt`;
    return {
      itemId: item.matchedItem.id,
      quantity,
      type,
      notes
    };
  });
}

export interface ParsedBarcodeQty {
  barkodas: string;
  kiekis: number;
  lineNumber: number;
}

/** Parse tab-separated supplier-return rows: Barkodas [TAB] kiekis (positive qty only). */
export function parseSupplierReturnData(text: string): ParsedBarcodeQty[] {
  const lines = text.trim().split('\n');
  const parsed: ParsedBarcodeQty[] = [];

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return;

    const parts = trimmedLine.split('\t').map((p) => p.trim());
    if (parts.length < 2) return;

    const kiekis = parseInt(parts[parts.length - 1], 10);
    const barkodas = parts.slice(0, -1).join(' ').trim();

    if (!barkodas || Number.isNaN(kiekis) || kiekis <= 0) return;

    parsed.push({
      barkodas,
      kiekis,
      lineNumber: index + 1
    });
  });

  return parsed;
}

/** Zip Barkodas / Kiekis column pastes (Stulpeliais). One line = one item; order must match. */
export function parseSupplierReturnColumns(
  barkodasText: string,
  kiekisText: string
): ParsedBarcodeQty[] {
  const barkodasLines = barkodasText.split('\n').map((l) => l.trim());
  const kiekisLines = kiekisText.split('\n').map((l) => l.trim());
  const maxLen = Math.max(barkodasLines.length, kiekisLines.length);
  const parsed: ParsedBarcodeQty[] = [];

  for (let i = 0; i < maxLen; i++) {
    const barkodas = barkodasLines[i] ?? '';
    if (!barkodas) continue;
    const kiekis = parseInt(kiekisLines[i] ?? '', 10);
    if (Number.isNaN(kiekis) || kiekis <= 0) continue;
    parsed.push({
      barkodas,
      kiekis,
      lineNumber: i + 1
    });
  }

  return parsed;
}

export function findSupplierReturnByBarkodas(
  barkodas: string,
  storageItems: SalesRegistrationStorageItem[]
): SalesRegistrationStorageItem | null {
  const trimmed = barkodas.trim();
  if (!trimmed) return null;
  return (
    storageItems.find((si) => si.barkodas != null && si.barkodas.trim() === trimmed) || null
  );
}

/** Match supplier-return paste rows against Sandėlyje stock only (positive qty, barkodas match). */
export function matchSupplierReturnItems(
  parsed: ParsedBarcodeQty[],
  storageItems: SalesRegistrationStorageItem[]
): ProcessedSale[] {
  const positive = parsed.filter((p) => p.kiekis > 0);

  const initial: ProcessedSale[] = positive.map((item) => {
    const matchedItem = findSupplierReturnByBarkodas(item.barkodas, storageItems);
    const currentQuantity = matchedItem ? matchedItem.quantity : 0;
    return {
      prekesKodas: item.barkodas,
      kiekis: item.kiekis,
      lineNumber: item.lineNumber,
      preke: matchedItem?.name ?? item.barkodas,
      matchedItem,
      currentQuantity,
      newQuantity: currentQuantity - item.kiekis,
      barkodas: item.barkodas,
      status: matchedItem ? ('matched' as const) : ('unmatched' as const)
    };
  });

  const aggregateByItem = new Map<string, { totalOut: number; currentQuantity: number }>();
  for (const row of initial) {
    if (!row.matchedItem) continue;
    const id = row.matchedItem.id;
    if (!aggregateByItem.has(id)) {
      aggregateByItem.set(id, { totalOut: 0, currentQuantity: row.currentQuantity });
    }
    aggregateByItem.get(id)!.totalOut += row.kiekis;
  }

  const pasteSimStock = new Map<string, number>();
  const newQuantityByLine = new Map<number, number>();
  for (const row of initial) {
    if (!row.matchedItem) {
      newQuantityByLine.set(row.lineNumber, row.newQuantity);
      continue;
    }
    const id = row.matchedItem.id;
    if (!pasteSimStock.has(id)) {
      pasteSimStock.set(id, row.currentQuantity);
    }
    const after = pasteSimStock.get(id)! - row.kiekis;
    pasteSimStock.set(id, after);
    newQuantityByLine.set(row.lineNumber, after);
  }

  const failedLines = new Set<number>();
  const regSimStock = new Map<string, number>();
  for (const row of initial) {
    if (!row.matchedItem) continue;
    const id = row.matchedItem.id;
    if (!regSimStock.has(id)) {
      regSimStock.set(id, row.currentQuantity);
    }
    const stock = regSimStock.get(id)!;
    if (stock < row.kiekis) {
      failedLines.add(row.lineNumber);
    } else {
      regSimStock.set(id, stock - row.kiekis);
    }
  }

  return initial.map((row) => {
    let status: ProcessedSale['status'] = row.matchedItem ? 'matched' : 'unmatched';
    if (row.matchedItem) {
      const agg = aggregateByItem.get(row.matchedItem.id)!;
      if (failedLines.has(row.lineNumber) || row.currentQuantity < agg.totalOut) {
        status = 'insufficient_stock';
      }
    }
    return {
      ...row,
      newQuantity: newQuantityByLine.get(row.lineNumber) ?? row.newQuantity,
      status
    };
  });
}

export interface SupplierReturnApiItem {
  itemId: string;
  quantity: number;
  notes: string;
}

export function buildSupplierReturnItems(
  processedReturns: ProcessedSale[]
): SupplierReturnApiItem[] {
  return processedReturns
    .filter(
      (ps): ps is ProcessedSale & { matchedItem: SalesRegistrationStorageItem } =>
        ps.status === 'matched' && ps.matchedItem != null && ps.kiekis > 0
    )
    .map((item) => ({
      itemId: item.matchedItem.id,
      quantity: item.kiekis,
      notes: `Grąžinta tiekėjui: -${item.kiekis} vnt`
    }));
}
